import { useEffect, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/utils/index";

const DISPLAY_FORMAT = "dd.MM.yyyy";
const DIGITS_FORMAT = "ddMMyyyy";

// "11081990" -> "11.08.1990"; inserts dots as the user types digits.
function maskDateDigits(digits: string): string {
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return [day, month, year].filter(Boolean).join(".");
}

// Parses "ddMMyyyy" digits into a Date, rejecting overflow (e.g. day 31 in February).
function parseDateDigits(digits: string): Date | undefined {
  if (digits.length !== DIGITS_FORMAT.length) return undefined;
  const parsed = parse(digits, DIGITS_FORMAT, new Date());
  if (!isValid(parsed) || format(parsed, DIGITS_FORMAT) !== digits) return undefined;
  return parsed;
}

interface DatePickerInputProps {
  value: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  fromYear?: number;
  toYear?: number;
  placeholder?: string;
  className?: string;
}

export function DatePickerInput({
  value,
  onSelect,
  disabled,
  fromYear,
  toYear,
  placeholder = "ДД.ММ.ГГГГ",
  className,
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ? format(value, DISPLAY_FORMAT) : "");
  const hasYearRange = fromYear !== undefined && toYear !== undefined;

  // Keep the typed text in sync when the value changes externally (e.g. calendar selection).
  useEffect(() => {
    setText(value ? format(value, DISPLAY_FORMAT) : "");
  }, [value]);

  const handleTextChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    setText(maskDateDigits(digits));
    if (digits.length === 0) {
      onSelect(undefined);
      return;
    }
    const parsed = parseDateDigits(digits);
    if (parsed) onSelect(parsed);
  };

  const handleBlur = () => {
    // If what's left doesn't resolve to a full valid date, fall back to the last valid value.
    const digits = text.replace(/\D/g, "");
    if (!parseDateDigits(digits)) {
      setText(value ? format(value, DISPLAY_FORMAT) : "");
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        value={text}
        placeholder={placeholder}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
        inputMode="numeric"
        className="pr-10"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:bg-transparent"
          >
            <CalendarIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              onSelect(date ?? undefined);
              setOpen(false);
            }}
            disabled={disabled}
            captionLayout={hasYearRange ? "dropdown" : "label"}
            fromYear={fromYear}
            toYear={toYear}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
